import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import TopNav from './TopNav';
import Sidebar, { AppPage } from './Sidebar';
import { DS } from '../../theme';

interface Project {
  id: number;
  name: string;
  dataset_count: number;
}

interface AppShellProps {
  children: React.ReactNode;
  activePage: AppPage;
  onNavigate: (page: AppPage) => void;
  onLogout: () => void;
  username?: string;
  projectName?: string;
  datasetName?: string;
  isAdmin?: boolean;
  hasDataset?: boolean;
  isGuest?: boolean;
  onGuestSignIn?: () => void;
  onGuestSignUp?: () => void;
  projects?: Project[];
  selectedProjectId?: number;
  onOpenProject?: (project: Project) => void;
}

const SIDEBAR_WIDTH = 220;

const AppShell: React.FC<AppShellProps> = ({
  children,
  activePage,
  onNavigate,
  onLogout,
  username,
  projectName,
  datasetName,
  isAdmin,
  hasDataset,
  isGuest,
  onGuestSignIn,
  onGuestSignUp,
  projects,
  selectedProjectId,
  onOpenProject,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleNavigate = (page: AppPage) => {
    onNavigate(page);
    if (isMobile) setMobileOpen(false);
  };

  const sidebarContent = (
    <Sidebar
      activePage={activePage}
      onNavigate={handleNavigate}
      projectName={projectName}
      datasetName={datasetName}
      isAdmin={isAdmin}
      hasDataset={hasDataset}
      projects={projects}
      selectedProjectId={selectedProjectId}
      onOpenProject={onOpenProject}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: DS.bg,
      }}
    >
      {/* ── Desktop sidebar — fixed left, full height ── */}
      {!isMobile && (
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            minWidth: SIDEBAR_WIDTH,
            height: '100%',
            flexShrink: 0,
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* ── Mobile sidebar — drawer overlay ── */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: SIDEBAR_WIDTH,
              backgroundColor: DS.bg2,
              borderRight: `1px solid ${DS.border}`,
            },
          }}
          sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.5)' } }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* ── Main column: topbar + scrollable content ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {/* Topbar — sticky at top of main column */}
        <TopNav
          username={username}
          onLogout={onLogout}
          onMenuToggle={() => setMobileOpen(!mobileOpen)}
          isMobile={isMobile}
          isGuest={isGuest}
          onGuestSignIn={onGuestSignIn}
          onGuestSignUp={onGuestSignUp}
        />

        {/* Scrollable page content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: DS.bg,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
